/**
 * P3-3 对账中心（admin / director）
 *
 * 三页签：
 *   1. 每日对账 —— 列出昨日 / 选定日期的对账明细
 *   2. 异常 —— 最近 7 天差异 >10% 的账号
 *   3. 平台信用 —— 当月各账号 A/B/C/D 评级
 */
import { useState } from 'react';
import { DatePicker, Input, InputNumber, Modal, Tabs, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';

export function ReconcileCenterPage() {
  const [date, setDate] = useState(dayjs().subtract(1, 'day'));
  // 支持 URL hash 跳转：/marketing/reconcile#manual 直接打开手动录入 tab
  const initialTab = typeof window !== 'undefined' && window.location.hash.includes('manual')
    ? 'manual' : 'daily';
  const [tab, setTab] = useState(initialTab);

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '对账中心']}
        title="📊 投流对账中心"
        subtitle="每日 09:00 自动对账 · 09:30 钉钉推送 · 差异 >10% 异常告警"
      />
      <div className="work-content">
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: 'daily', label: '每日对账', children: <DailyTab date={date} setDate={setDate} /> },
            { key: 'disc', label: '异常（>10% 差异）', children: <DiscrepancyTab /> },
            { key: 'credit', label: '平台信用', children: <CreditTab /> },
            { key: 'manual', label: '手动录入', children: <ManualEntryTab /> },
          ]}
        />
      </div>
    </>
  );
}

function DailyTab({ date, setDate }: any) {
  const { data: accounts } = useQuery<any[]>({
    queryKey: ['marketing-accounts'],
    queryFn: () => $.get('/marketing/accounts'),
  });
  const dateStr = date.format('YYYY-MM-DD');
  const { data: rows, refetch } = useQuery<any[]>({
    queryKey: ['reconcile-daily', dateStr],
    queryFn: () => $.get(`/marketing-reconcile/daily?date=${dateStr}`),
  });
  const acctMap = new Map<number, any>();
  (accounts ?? []).forEach((a: any) => acctMap.set(Number(a.id), a));

  const [reviewing, setReviewing] = useState<any>(null);
  const [reviewStatus, setReviewStatus] = useState('reviewed');
  const [reviewNotes, setReviewNotes] = useState('');

  const qc = useQueryClient();
  const review = useMutation({
    mutationFn: (id: number) => $.post(`/marketing-reconcile/${id}/review`, {
      status: reviewStatus, notes: reviewNotes,
    }),
    onSuccess: () => {
      message.success('已审核');
      setReviewing(null); setReviewNotes('');
      qc.invalidateQueries({ queryKey: ['reconcile-daily'] });
    },
    onError: (e: any) => message.error(e?.message ?? '审核失败'),
  });

  const [entry, setEntry] = useState<any>(null);
  const upsertEntry = useMutation({
    mutationFn: () => $.post(`/marketing-reconcile/manual-entry`, entry),
    onSuccess: () => {
      message.success('已录入');
      setEntry(null);
      refetch();
    },
    onError: (e: any) => message.error(e?.message ?? '录入失败'),
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <DatePicker value={date} onChange={(d) => d && setDate(d)} allowClear={false} />
        <Btn kind="default" onClick={() => refetch()}>刷新</Btn>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
          共 {rows?.length ?? 0} 个账号
        </span>
      </div>
      <HomeCard title={`${dateStr} 对账明细`}>
        <TableWrap>
          <table className="ck-table" style={{ width: '100%', minWidth: 820 }}>
            <thead>
              <tr>
                <th>账号</th>
                <th style={{ textAlign: 'right' }}>花费</th>
                <th style={{ textAlign: 'right' }}>推报 / 实收</th>
                <th style={{ textAlign: 'right' }}>建客</th>
                <th style={{ textAlign: 'right' }}>差异</th>
                <th>状态</th>
                <th>来源</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                  暂无对账数据
                </td></tr>
              )}
              {(rows ?? []).map((r: any) => {
                const acct = acctMap.get(Number(r.accountId));
                const disc = Number(r.discrepancyPercent);
                return (
                  <tr key={r.id}>
                    <td>{acct?.accountName ?? `#${r.accountId}`}</td>
                    <td className="money" style={{ textAlign: 'right' }}>¥{Number(r.platformReportedSpent).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{r.platformReportedLeads} / {r.crmReceivedLeads}</td>
                    <td style={{ textAlign: 'right' }}>{r.crmCreatedCustomers}</td>
                    <td style={{ textAlign: 'right' }}>
                      {disc > 10
                        ? <Pill tone="danger">⚠️ {disc}%</Pill>
                        : <Pill tone="success">{disc}%</Pill>}
                    </td>
                    <td><Pill tone={r.status === 'approved' ? 'success' : r.status === 'disputed' ? 'danger' : 'default'}>{r.status}</Pill></td>
                    <td><span style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.dataSource}</span></td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <Btn kind="default" onClick={() => setReviewing(r)}>审核</Btn>
                      <Btn kind="default" onClick={() => setEntry({
                        accountId: Number(r.accountId),
                        entryDate: dateStr,
                        spent: Number(r.platformReportedSpent),
                        leads: r.platformReportedLeads,
                        msgUsers: r.platformReportedMsgUsers,
                      })}>录入</Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      </HomeCard>

      <Modal
        title={`审核对账 #${reviewing?.id ?? ''}`}
        open={!!reviewing}
        onCancel={() => setReviewing(null)}
        footer={
          <>
            <Btn kind="default" onClick={() => setReviewing(null)}>取消</Btn>
            <Btn kind="primary" onClick={() => review.mutate(reviewing.id)} disabled={review.isPending}>
              {review.isPending ? '提交中…' : '保存审核'}
            </Btn>
          </>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>状态</div>
          <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}
                  style={{ width: '100%', height: 32, padding: '0 10px' }}>
            <option value="reviewed">已查看</option>
            <option value="approved">认可（差异合理）</option>
            <option value="disputed">争议（要追平台）</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>备注</div>
          <Input.TextArea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={3}
                          placeholder="如：平台少推 3 条 / 真实流失 / 等等" />
        </div>
      </Modal>

      <Modal
        title="录入 / 修正平台数据"
        open={!!entry}
        onCancel={() => setEntry(null)}
        footer={
          <>
            <Btn kind="default" onClick={() => setEntry(null)}>取消</Btn>
            <Btn kind="primary" onClick={() => upsertEntry.mutate()} disabled={upsertEntry.isPending}>
              {upsertEntry.isPending ? '保存中…' : '保存'}
            </Btn>
          </>
        }
      >
        {entry && (
          <>
            <Row label="账号" value={`#${entry.accountId}`} />
            <Row label="日期" value={entry.entryDate} />
            <NumberRow label="花费 ¥" value={entry.spent}
                       onChange={(v) => setEntry({ ...entry, spent: v })} />
            <NumberRow label="推报线索数" value={entry.leads}
                       onChange={(v) => setEntry({ ...entry, leads: v })} />
            <NumberRow label="推报私信数" value={entry.msgUsers}
                       onChange={(v) => setEntry({ ...entry, msgUsers: v })} />
          </>
        )}
      </Modal>
    </>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}

function NumberRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, marginBottom: 4 }}>{label}</div>
      <InputNumber value={value} onChange={(v) => onChange(Number(v ?? 0))} style={{ width: '100%' }} />
    </div>
  );
}

function DiscrepancyTab() {
  const { data: rows } = useQuery<any[]>({
    queryKey: ['reconcile-discrepancies'],
    queryFn: () => $.get('/marketing-reconcile/discrepancies?days=7'),
  });
  const { data: accounts } = useQuery<any[]>({
    queryKey: ['marketing-accounts'],
    queryFn: () => $.get('/marketing/accounts'),
  });
  const acctMap = new Map<number, any>();
  (accounts ?? []).forEach((a: any) => acctMap.set(Number(a.id), a));

  return (
    <HomeCard title="最近 7 天差异 >10%">
      <TableWrap>
        <table className="ck-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>日期</th>
              <th>账号</th>
              <th style={{ textAlign: 'right' }}>推报 / 实收</th>
              <th style={{ textAlign: 'right' }}>差异条数</th>
              <th style={{ textAlign: 'right' }}>差异 %</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--success)' }}>
                ✅ 没有异常 — 各账号差异均在 10% 内
              </td></tr>
            )}
            {(rows ?? []).map((r: any) => (
              <tr key={r.id}>
                <td>{r.reconcileDate}</td>
                <td>{acctMap.get(Number(r.accountId))?.accountName ?? `#${r.accountId}`}</td>
                <td style={{ textAlign: 'right' }}>{r.platformReportedLeads} / {r.crmReceivedLeads}</td>
                <td style={{ textAlign: 'right' }}>{r.discrepancyAmount}</td>
                <td style={{ textAlign: 'right' }}><Pill tone="danger">{r.discrepancyPercent}%</Pill></td>
                <td><Pill tone={r.status === 'approved' ? 'success' : 'default'}>{r.status}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </HomeCard>
  );
}

function CreditTab() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const { data: rows } = useQuery<any[]>({
    queryKey: ['platform-credit', month],
    queryFn: () => $.get(`/platform-credit-rating?month=${month}`),
  });
  const { data: accounts } = useQuery<any[]>({
    queryKey: ['marketing-accounts'],
    queryFn: () => $.get('/marketing/accounts'),
  });
  const acctMap = new Map<number, any>();
  (accounts ?? []).forEach((a: any) => acctMap.set(Number(a.id), a));

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Input value={month} onChange={(e) => setMonth(e.target.value)}
               placeholder="YYYY-MM" style={{ width: 160 }} />
      </div>
      <HomeCard title={`${month} 平台信用评级`}>
        <TableWrap>
          <table className="ck-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>账号</th>
                <th style={{ textAlign: 'right' }}>推报总量</th>
                <th style={{ textAlign: 'right' }}>实收总量</th>
                <th style={{ textAlign: 'right' }}>准确率</th>
                <th style={{ textAlign: 'right' }}>信用分</th>
                <th>评级</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                  当月暂无评级（每月 1 号自动算上月）
                </td></tr>
              )}
              {(rows ?? []).map((r: any) => (
                <tr key={r.id}>
                  <td>{acctMap.get(Number(r.accountId))?.accountName ?? `#${r.accountId}`}</td>
                  <td style={{ textAlign: 'right' }}>{r.totalReportedLeads}</td>
                  <td style={{ textAlign: 'right' }}>{r.totalActualLeads}</td>
                  <td style={{ textAlign: 'right' }}>{(Number(r.accuracyRate) * 100).toFixed(2)}%</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.trustScore).toFixed(2)}</td>
                  <td>
                    <Pill tone={r.rating === 'A' ? 'success' : r.rating === 'D' ? 'danger' : 'default'}>
                      {r.rating}
                    </Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </HomeCard>
    </>
  );
}


function ManualEntryTab() {
  const qc = useQueryClient();
  const [entry, setEntry] = useState<any>(null);

  const { data: accounts } = useQuery<any[]>({
    queryKey: ["marketing-accounts"],
    queryFn: () => $.get("/marketing/accounts"),
  });
  const { data: rows } = useQuery<any[]>({
    queryKey: ["manual-entries-list"],
    queryFn: () => $.get("/marketing-reconcile/manual-entries"),
  });
  const acctMap = new Map<number, any>();
  (accounts ?? []).forEach((a: any) => acctMap.set(Number(a.id), a));

  const upsert = useMutation({
    mutationFn: () => $.post(`/marketing-reconcile/manual-entry`, entry),
    onSuccess: () => {
      message.success("已保存");
      setEntry(null);
      qc.invalidateQueries({ queryKey: ["manual-entries-list"] });
      qc.invalidateQueries({ queryKey: ["reconcile-daily"] });
    },
    onError: (e: any) => message.error(e?.message ?? "保存失败"),
  });

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Btn kind="primary" onClick={() => setEntry({
          accountId: accounts?.[0]?.id ? Number(accounts[0].id) : 0,
          entryDate: dayjs().format("YYYY-MM-DD"),
          spent: 0, leads: 0, msgUsers: 0,
        })}>+ 新增录入</Btn>
      </div>
      <HomeCard title={`手动录入历史（最近 ${rows?.length ?? 0} 条）`}>
        <TableWrap>
          <table className="ck-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>日期</th><th>账号</th><th style={{ textAlign: 'right' }}>花费</th><th style={{ textAlign: 'right' }}>线索</th><th style={{ textAlign: 'right' }}>私信</th><th>备注</th><th>录入人</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "var(--text-3)" }}>
                  暂无手动录入数据
                </td></tr>
              )}
              {(rows ?? []).map((r: any) => (
                <tr key={r.id}>
                  <td>{r.entryDate}</td>
                  <td>{acctMap.get(Number(r.accountId))?.accountName ?? `#${r.accountId}`}</td>
                  <td style={{ textAlign: 'right' }}>¥{Number(r.spent).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{r.leads}</td>
                  <td style={{ textAlign: 'right' }}>{r.msgUsers}</td>
                  <td>{r.notes ?? "—"}</td>
                  <td>#{r.enteredByUserId ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </HomeCard>

      <Modal
        title="录入 / 修正平台数据"
        open={!!entry}
        onCancel={() => setEntry(null)}
        footer={
          <>
            <Btn kind="default" onClick={() => setEntry(null)}>取消</Btn>
            <Btn kind="primary" onClick={() => upsert.mutate()} disabled={upsert.isPending}>
              {upsert.isPending ? "保存中…" : "保存"}
            </Btn>
          </>
        }
      >
        {entry && (
          <>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>账号</div>
              <select value={entry.accountId}
                      onChange={(e) => setEntry({ ...entry, accountId: Number(e.target.value) })}
                      style={{ width: "100%", height: 32, padding: '0 10px' }}>
                {(accounts ?? []).map((a: any) => (
                  <option key={a.id} value={a.id}>{a.accountName}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>日期 (YYYY-MM-DD)</div>
              <Input value={entry.entryDate}
                     onChange={(e) => setEntry({ ...entry, entryDate: e.target.value })} />
            </div>
            <NumberRow label="花费 ¥" value={entry.spent}
                       onChange={(v) => setEntry({ ...entry, spent: v })} />
            <NumberRow label="推报线索数" value={entry.leads}
                       onChange={(v) => setEntry({ ...entry, leads: v })} />
            <NumberRow label="推报私信数" value={entry.msgUsers}
                       onChange={(v) => setEntry({ ...entry, msgUsers: v })} />
          </>
        )}
      </Modal>
    </>
  );
}
